import { ReactNode } from 'react';
import { IconName } from '@ardoq/icons';
import { dispatchAction } from '@ardoq/rxbeach';
import { componentInterface } from '@ardoq/component-interface';
import { scopeDataOperations } from '@ardoq/scope-data';
import { forceUpdateComponent } from 'streams/context/ContextActions';
import {
  lockComponents,
  unlockComponents,
} from 'streams/components/ComponentActions';
import {
  APIComponentAttributes,
  APIEntityType,
  APIReferenceAttributes,
  ArdoqId,
  ProvenanceSource,
  ReferenceDirection,
} from '@ardoq/api-types';
import { toIds } from '@ardoq/core';
import {
  type ReferenceComponents,
  type ReferenceEndpoints,
} from './referenceComponentsInterface';
import { alert } from '@ardoq/modal';
import {
  ComponentPropertiesGetContentOptions,
  GetContentOptions,
  GetContentOptionsType,
  ReferencePropertiesGetContentOptions,
} from 'appModelStateEdit/legacyTypes';
import { showRightPane } from 'appContainer/actions';
import {
  DropdownItem,
  DropdownOption,
  DropdownOptionType,
  DropdownSubmenu,
} from '@ardoq/dropdown-menu';
import { getActiveScenarioId } from 'streams/activeScenario/activeScenario$';
import {
  TrackingFunction,
  deleteModels,
  includeComponentsOfType,
  createReferencesFromSourceFilter,
  createReferencesToTargetFilter,
  excludeChildComponents,
  excludeComponentsOfType,
  excludeContentFromWorkspace,
  excludeReferencesFromWorkspace,
  excludeReferencesOfType,
  excludeReferencesToWorkspace,
  excludeSelected,
  getPermissionContextUserId,
  getReferenceDisplayName,
  includeReferencesOfType,
} from './utils';
import type { PermissionContext } from '@ardoq/access-control';
import { componentAccessControlOperation } from 'resourcePermissions/accessControlHelpers/component';
import { referenceAccessControlOperation } from 'resourcePermissions/accessControlHelpers/reference';
import { allComponentsInScopeAreSelected } from 'appContainer/MainAppModule/MainAppModuleSidebar/scenarioRelated/navigator/ScenarioRelatedContextMenu';
import {
  addComponentsToScenario,
  removeComponentsFromScenario,
} from 'appContainer/MainAppModule/MainAppModuleSidebar/scenarioRelated/navigator/actionWrappers';
import {
  TrackedCompAndRefContextMenuLabels,
  TrackedComponentContextMenuLabels,
  TrackedReferenceContextMenuLabels,
  TrackedScenarioContextComponentContextMenuLabels,
  TrackedTagContextMenuLabels,
  TrackedWorkspaceContextMenuLabels,
} from './tracking';
import { isScenarioMode } from 'models/utils/scenarioUtils';
import { isTruthy } from '@ardoq/common-helpers';
import {
  lockReferences,
  reverseReferences,
  selectReference,
  unlockReferences,
} from 'streams/references/ReferenceActions';
import { initEntityMerge } from 'components/EntityMerge/actions';
import { Features } from '@ardoq/features';
import { deleteTag, editTag, filterTag } from 'streams/tags/TagActions';
import { openViewpointBuilderWithComponents } from 'viewpointBuilder/actions';
import { openApplySavedViewpointModalForComponentIds } from '../traversals/selectTraversalForContextComponentsModal/actions';
import { uniq } from 'lodash';
import { WithPopover } from '@ardoq/popovers';
import styled from 'styled-components';
import { navigateToAuditLog } from 'router/navigationActions';
import { trackAuditLogEntryPoint } from '../auditLog/tracking';
import { openDetailsDrawer } from 'appLayout/ardoqStudio/detailsDrawer/actions';
import { openViewInferenceWindow } from 'traversals/viewInference/actions';
import { InlineText, Paragraph } from '@ardoq/typography';
import { Stack } from '@ardoq/layout';
import { startCreateReferences } from 'createReferences2024/createReferencesActions';
import { ContextMenuSourceNode } from '@ardoq/context-menu';
import { resolveSourceHandle } from 'dynamicReferenceCreation/sourceAnchor';
import { openDocumentPicker } from 'integrations/lucidchart/actions';
import { TOGGLE_LOCK_MENU_ITEM_KEY } from './consts';
import { navigate } from '../router/declarative/streamRouterAdapter/actions';
import { excludeWorkspaceFromScenario } from 'streams/scenarios/actions';
import { type WorkspaceAndModelLookup } from '@ardoq/data-model';
import { getReferenceTypeNameFromModels } from './referenceTypeNameFromModels';
import { openWorkspace } from 'streams/workspaces/actions';
import {
  getReverseConstraintViolations,
  type ReverseConstraintViolation,
} from './reverseReferenceConstraintViolations';
import { type ReferenceCreationMenuState } from 'streams/referenceCreationMenuState$';
import { openComponentOverviewDrawer } from 'componentOverview2025/componentOverview2025Drawer/openComponentOverviewDrawer';
import { requestOpenAddChildDialog } from '../addChildDialog/addChildDialogActions';
import { colors } from '@ardoq/design-tokens';
import { hasFeature } from 'modelInterface/features/featureInterface';
import { isDiscoverJourney } from 'canvas/isDiscoverJourney';
import { v4 as uuidv4 } from 'uuid';
import { registerNewDataset } from 'canvas/sidebarPanel/sidebars/improvedDatasetsPanel/registerNewDataset';
import { getFeatureContext } from 'streams/featureContext$';
import { lockOperations } from 'lockedComponents/lockOperations';
import { excludeComponentFromViewpoint } from 'loadedState/excludeComponent/actions';
import { dynamicReferenceCreationArmed } from 'dynamicReferenceCreation/dynamicReferenceCreationActions';
import { BlocksViewNode, Controller } from 'tabview/blocks/types';
import { openSidebarPanel } from 'canvas/sidebarPanel/actions';
import { SidebarPanelContentIds } from 'canvas/sidebarPanel/types';
import { t, type I18nLocale } from '@ardoq/i18n';
import { createReferencesForComponentsRequested } from 'canvas/sidebarPanel/sidebars/createReferences/actions';
import { createReferencesTracking } from 'canvas/sidebarPanel/sidebars/createReferences/createReferencesTracking';

export const getEditPropertiesMenuItem = (
  componentIds: ArdoqId[],
  referenceIds: ArdoqId[],
  trackingFn: TrackingFunction,
  i18nLocale: I18nLocale,
  isViewpointMode: boolean
): DropdownOption | DropdownSubmenu | null => {
  const isHybrid = componentIds.length > 0 && referenceIds.length > 0;
  const name = 'edit-properties';
  const label = t(i18nLocale, 'contextMenus:editProperties', 'Edit properties');
  const iconName = IconName.EDIT;
  const isCanvasPrototype =
    isViewpointMode && hasFeature(Features.CANVAS_PROTOTYPE);
  const editComponentPropertiesArgs: ComponentPropertiesGetContentOptions | null =
    componentIds.length
      ? {
          type: GetContentOptionsType.COMPONENT_PROPERTIES,
          componentIds,
        }
      : null;
  const editReferencePropertiesArgs: ReferencePropertiesGetContentOptions | null =
    referenceIds.length
      ? {
          type: GetContentOptionsType.REFERENCE_PROPERTIES,
          referenceIds,
        }
      : null;

  if (isHybrid) {
    return {
      name,
      label,
      iconName,
      type: DropdownOptionType.SUBMENU,
      options: [
        {
          name: 'edit-component-properties',
          testId: 'edit-component-properties-item',
          label: t(
            i18nLocale,
            'contextMenus:editSelectedComponents',
            'Edit selected components'
          ),
          iconName: IconName.NONE,
          type: DropdownOptionType.OPTION,
          onClick: () => {
            trackingFn(
              TrackedCompAndRefContextMenuLabels.EDIT_SELECTED_COMPONENT_PROPS
            );
            dispatchAction(showRightPane(editComponentPropertiesArgs!));
          },
        },
        {
          name: 'edit-reference-properties',
          testId: 'edit-reference-properties-item',
          label: t(
            i18nLocale,
            'contextMenus:editSelectedReferences',
            'Edit selected references'
          ),
          iconName: IconName.NONE,
          type: DropdownOptionType.OPTION,
          onClick: () => {
            trackingFn(
              TrackedCompAndRefContextMenuLabels.EDIT_SELECTED_REFERENCE_PROPS
            );
            dispatchAction(showRightPane(editReferencePropertiesArgs!));
          },
        },
      ],
    };
  }
  if (componentIds.length > 0) {
    return {
      name,
      label: isCanvasPrototype
        ? t(i18nLocale, 'contextMenus:viewAndEdit', 'View and edit')
        : label,
      iconName,
      testId: 'edit-components-properties-item',
      onClick: () => {
        trackingFn(TrackedComponentContextMenuLabels.EDIT_COMPONENT);
        if (isCanvasPrototype) {
          openComponentOverviewDrawer({
            componentId: componentIds[0],
            trackingLocation: 'contextMenu',
            startTab: 'overview',
          });
        } else {
          dispatchAction(showRightPane(editComponentPropertiesArgs!));
        }
      },
      type: DropdownOptionType.OPTION,
    };
  }
  return {
    name,
    label: isCanvasPrototype
      ? t(i18nLocale, 'contextMenus:editReference', 'Edit reference')
      : label,
    iconName,
    testId: 'edit-references-properties-item',
    onClick: () => {
      trackingFn(TrackedReferenceContextMenuLabels.EDIT_REFERENCE);
      dispatchAction(showRightPane(editReferencePropertiesArgs!));
    },
    type: DropdownOptionType.OPTION,
  };
};

export const getEditStyleMenuItem = (
  componentIds: ArdoqId[],
  referenceIds: ArdoqId[],
  trackingFn: TrackingFunction,
  i18nLocale: I18nLocale,
  isViewpointMode: boolean
) => {
  const isHybrid = componentIds.length && referenceIds.length;
  return {
    name: 'edit-style',
    label: t(i18nLocale, 'contextMenus:editStyle', 'Edit style'),
    iconName: IconName.PALETTE,
    type: isHybrid ? DropdownOptionType.SUBMENU : DropdownOptionType.OPTION,
    ...(isHybrid
      ? {
          options: [
            {
              name: 'edit-component-style',
              testId: 'edit-style-item',
              label: t(
                i18nLocale,
                'contextMenus:editSelectedComponents',
                'Edit selected components'
              ),
              iconName: IconName.PALETTE,
              type: DropdownOptionType.OPTION,
              onClick: () => {
                trackingFn(
                  TrackedCompAndRefContextMenuLabels.EDIT_SELECTED_COMPONENT_STYLE
                );
                dispatchAction(
                  showRightPane({
                    type: GetContentOptionsType.COMPONENT_STYLE,
                    componentIds,
                  })
                );
              },
            },
          ],
        }
      : {
          testId: 'edit-style-item',
          onClick: () => {
            trackingFn(TrackedComponentContextMenuLabels.EDIT_COMPONENT_STYLE);
            if (isViewpointMode && hasFeature(Features.CANVAS_PROTOTYPE)) {
              openComponentOverviewDrawer({
                componentId: componentIds[0],
                trackingLocation: 'contextMenu',
                startTab: 'styles',
              });
            } else {
              dispatchAction(
                showRightPane({
                  type: GetContentOptionsType.COMPONENT_STYLE,
                  componentIds,
                })
              );
            }
          },
        }),
  };
};

const handleAddChildComponentClick = (
  component: APIComponentAttributes,
  trackingFn: TrackingFunction,
  isViewpointMode: boolean,
  provenanceSource?: ProvenanceSource
): void => {
  trackingFn(TrackedComponentContextMenuLabels.ADD_CHILD_TO_COMPONENT);
  // KFQ12026
  if (isViewpointMode && hasFeature(Features.CANVAS_PROTOTYPE)) {
    dispatchAction(
      requestOpenAddChildDialog({
        componentId: component._id,
        provenanceSource,
      })
    );
  } else {
    const payload: GetContentOptions = {
      type: GetContentOptionsType.CREATE_COMPONENT,
      parentId: component._id,
      workspaceId: component.rootWorkspace,
    };
    dispatchAction(showRightPane(payload));
  }
};

type GetAddChildComponentMenuItemArgs = {
  component: APIComponentAttributes;
  canHaveChildren: boolean;
  trackingFn: TrackingFunction;
  isViewpointMode: boolean;
  i18nLocale: I18nLocale;
  provenanceSource?: ProvenanceSource;
};

export const getAddChildComponentMenuItem = ({
  component,
  canHaveChildren,
  trackingFn,
  isViewpointMode,
  i18nLocale,
  provenanceSource,
}: GetAddChildComponentMenuItemArgs) => ({
  name: `add-child`,
  testId: `add-child-item`,
  label: isViewpointMode
    ? t(i18nLocale, 'contextMenus:addChildComponent', 'Add child component')
    : t(i18nLocale, 'contextMenus:addChild', 'Add child'),
  iconName: IconName.ADD,
  isDisabled: !canHaveChildren,
  onClick: () =>
    handleAddChildComponentClick(
      component,
      trackingFn,
      isViewpointMode,
      provenanceSource
    ),
  type: DropdownOptionType.OPTION,
});

export const addComponentToScenario = (
  componentIds: ArdoqId[],
  trackingFn: TrackingFunction,
  i18nLocale: I18nLocale
) => ({
  name: 'include-in-scenario',
  testId: 'include-in-scenario-item',
  label: t(i18nLocale, 'contextMenus:includeInScenario', 'Include in scenario'),
  iconName: IconName.ADD,
  onClick: () => {
    trackingFn(
      TrackedScenarioContextComponentContextMenuLabels.INCLUDE_IN_SCENARIO
    );
    addComponentsToScenario(componentIds);
  },
  type: DropdownOptionType.OPTION,
});

type GetToggleLockMenuItemArgs = {
  components: APIComponentAttributes[];
  references: APIReferenceAttributes[];
  trackingFn: TrackingFunction;
  i18nLocale: I18nLocale;
  permissionContext: PermissionContext;
};

export const getToggleLockMenuItem = ({
  components,
  references,
  trackingFn,
  i18nLocale,
  permissionContext,
}: GetToggleLockMenuItemArgs): DropdownOption | null => {
  const locks = [...components, ...references].map(entity => entity.lock);
  const featureContext = getFeatureContext();
  if (!lockOperations.isLockingEnabled(featureContext)) return null;
  const hasLockedEntity = lockOperations.isAnyLocked(locks, featureContext);

  const componentIds = toIds(components);
  const referenceIds = toIds(references);

  const currentUserId = getPermissionContextUserId(permissionContext);

  const areSomeComponentsCantBeUnlocked = components.some(
    component =>
      !componentAccessControlOperation.canUnlock({
        permissionContext,
        component,
      })
  );

  const areSomeReferencesCantBeUnlocked = references.some(
    reference =>
      !referenceAccessControlOperation.canUnlock({
        permissionContext,
        reference,
      })
  );

  const isDisabled =
    hasLockedEntity &&
    (areSomeComponentsCantBeUnlocked || areSomeReferencesCantBeUnlocked);

  const isMixedEntities = referenceIds.length > 0 && componentIds.length > 0;
  const isOnlyReferences = referenceIds.length > 0 && componentIds.length === 0;
  const isSingleReference = isOnlyReferences && referenceIds.length === 1;

  const trackingOptionTitle = isMixedEntities
    ? TrackedCompAndRefContextMenuLabels.TOGGLE_LOCK
    : isOnlyReferences
      ? TrackedReferenceContextMenuLabels.LOCK_REFERENCE
      : TrackedComponentContextMenuLabels.LOCK_COMPONENT;

  const label = (() => {
    if (isSingleReference) {
      return hasLockedEntity
        ? t(i18nLocale, 'contextMenus:unlockReference', 'Unlock reference')
        : t(i18nLocale, 'contextMenus:lockReference', 'Lock reference');
    }
    if (isOnlyReferences) {
      return hasLockedEntity
        ? t(i18nLocale, 'contextMenus:unlockReferences', 'Unlock references')
        : t(i18nLocale, 'contextMenus:lockReferences', 'Lock references');
    }
    return hasLockedEntity
      ? t(i18nLocale, 'contextMenus:unlockSingle', 'Unlock')
      : t(i18nLocale, 'contextMenus:lockSingle', 'Lock');
  })();

  return {
    name: TOGGLE_LOCK_MENU_ITEM_KEY,
    testId: `toggle-lock-item`,
    label,
    iconName: hasLockedEntity ? IconName.LOCK_OPEN : IconName.LOCK,
    onClick: () => {
      trackingFn(trackingOptionTitle);
      if (componentIds.length > 0) {
        if (hasLockedEntity) {
          dispatchAction(unlockComponents(componentIds));
        } else if (currentUserId) {
          dispatchAction(
            lockComponents({
              componentIds,
              userId: currentUserId,
            })
          );
        }
      }
      if (referenceIds.length === 0) return;
      if (hasLockedEntity) {
        dispatchAction(unlockReferences(referenceIds));
      } else if (currentUserId) {
        dispatchAction(
          lockReferences({
            referenceIds,
            userId: currentUserId,
          })
        );
      }
    },
    type: DropdownOptionType.OPTION,
    isDisabled,
  };
};

export const getCreateReferenceMenuItem = (
  componentIds: ArdoqId[],
  event: Event | null,
  x: number,
  y: number,
  trackingFn: TrackingFunction,
  i18nLocale: I18nLocale,
  isViewpointMode: boolean,
  eventTargetModelId?: string,
  isStartInNavigator?: boolean
) => {
  if (document.fullscreenElement instanceof HTMLElement) {
    return null;
  }

  const multiselect = componentIds.length > 1;
  const isCreateReferencesSidebarMounted =
    isViewpointMode &&
    (isDiscoverJourney() || hasFeature(Features.CANVAS_PROTOTYPE));
  if (multiselect && !isCreateReferencesSidebarMounted) {
    return {
      name: 'create-references',
      label: t(i18nLocale, 'contextMenus:createReference', 'Create reference'),
      iconName: IconName.REFERENCE,
      type: DropdownOptionType.SUBMENU,
      options: [
        {
          name: 'create-outgoing-reference',
          testId: 'create-outgoing-reference-item',
          label: t(
            i18nLocale,
            'contextMenus:createOutgoingReference',
            'Create outgoing reference'
          ),
          iconName: IconName.ARROW_FORWARD,
          type: DropdownOptionType.OPTION,
          onClick: () => {
            trackingFn(
              TrackedComponentContextMenuLabels.CREATE_OUTGOING_REFERENCE
            );
            dispatchAction(
              startCreateReferences({
                event,
                linkSourceIds: componentIds,
                startPosition: { x, y },
                refDirection: ReferenceDirection.OUTGOING,
                linkSourceNodeId: eventTargetModelId,
                isStartInNavigator,
              })
            );
          },
        },
        {
          name: 'create-incoming-reference',
          testId: 'create-incoming-reference-item',
          label: t(
            i18nLocale,
            'contextMenus:createIncomingReference',
            'Create incoming reference'
          ),
          iconName: IconName.ARROW_BACK,
          type: DropdownOptionType.OPTION,
          onClick: () => {
            trackingFn(
              TrackedComponentContextMenuLabels.CREATE_INCOMING_REFERENCE
            );
            dispatchAction(
              startCreateReferences({
                event,
                linkSourceIds: componentIds,
                startPosition: { x, y },
                refDirection: ReferenceDirection.INCOMING,
                linkSourceNodeId: eventTargetModelId,
                isStartInNavigator,
              })
            );
          },
        },
      ],
    };
  }

  return {
    name: `create-reference`,
    testId: `create-reference-item`,
    label: t(i18nLocale, 'contextMenus:createReference', 'Create reference'),
    iconName: IconName.REFERENCE,
    type: DropdownOptionType.OPTION,
    onClick: () => {
      trackingFn(TrackedComponentContextMenuLabels.CREATE_REFERENCE);
      if (isCreateReferencesSidebarMounted) {
        createReferencesTracking.trackOpenPanel('contextMenu');
        dispatchAction(
          openSidebarPanel(SidebarPanelContentIds.CREATE_REFERENCES)
        );
        dispatchAction(createReferencesForComponentsRequested(componentIds));
        return;
      }
      dispatchAction(
        startCreateReferences({
          event,
          linkSourceIds: componentIds,
          startPosition: { x, y },
          refDirection: ReferenceDirection.OUTGOING,
          linkSourceNodeId: eventTargetModelId,
          isStartInNavigator,
        })
      );
    },
  };
};

const withTruncateTitle = (option: DropdownOption) => ({
  ...option,
  truncateTitle: true,
});

type GetComponentNavigateToMenuItemArgs = {
  component: APIComponentAttributes;
  parent: APIComponentAttributes | null;
  trackingFn: TrackingFunction;
  i18nLocale: I18nLocale;
  isScenarioContextComponent?: boolean;
};

export const getComponentNavigateToMenuItem = ({
  component,
  parent,
  trackingFn,
  i18nLocale,
  isScenarioContextComponent,
}: GetComponentNavigateToMenuItemArgs): DropdownSubmenu => {
  const componentId = component._id;

  const TrackedMenuLabels = isScenarioContextComponent
    ? TrackedScenarioContextComponentContextMenuLabels
    : TrackedComponentContextMenuLabels;

  const navigateToProps = { name: 'navigate-to', iconName: IconName.NAVIGATE };
  const navigateToButtonProps = {
    type: DropdownOptionType.OPTION,
    testId: 'go-to-target-item',
    onClick: () => {
      trackingFn(TrackedMenuLabels.SELECT);
      dispatchAction(
        forceUpdateComponent({
          cid: componentId,
          trackingLocation: 'contextMenu',
        })
      );
    },
  };

  const navigateToParentMenuItem = parent && {
    name: 'go-to-parent',
    testId: 'go-to-parent-item',
    label: t(
      i18nLocale,
      'contextMenus:goToParent',
      { name: parent.name },
      'Go to parent {{name}}'
    ),
    type: DropdownOptionType.OPTION,
    onClick: () => {
      trackingFn(TrackedMenuLabels.SELECT_PARENT);
      dispatchAction(
        forceUpdateComponent({
          cid: parent._id,
          trackingLocation: 'contextMenu',
        })
      );
    },
  };

  const GO_TO_COMPONENTNAME = t(
    i18nLocale,
    'contextMenus:goToName',
    { name: component.name },
    'Go to {{name}}'
  );

  const navigateToWorkspaceMenuItem: DropdownOption = {
    name: 'go-to-workspace',
    testId: 'go-to-workspace-item',
    label: t(i18nLocale, 'contextMenus:goToWorkspace', 'Go to workspace'),
    type: DropdownOptionType.OPTION,
    onClick: () => {
      trackingFn(TrackedMenuLabels.SELECT_WORKSPACE);
      dispatchAction(
        openWorkspace({
          workspaceId: component.rootWorkspace,
          keepCurrentContext: false,
          trackingLocation: 'contextMenu',
        })
      );
    },
  };

  return {
    ...navigateToProps,
    label: t(i18nLocale, 'contextMenus:navigateTo', 'Navigate to'),
    type: DropdownOptionType.SUBMENU,
    options: [
      {
        ...navigateToButtonProps,
        name: 'go-to-target',
        label: GO_TO_COMPONENTNAME,
      },

      navigateToParentMenuItem || navigateToWorkspaceMenuItem,
    ].map(withTruncateTitle),
  };
};

const openNewDatasetFromSelection = (componentIds: ArdoqId[]): void => {
  if (!hasFeature(Features.IMPROVED_DATASET_DEFINITION)) {
    dispatchAction(openViewpointBuilderWithComponents({ componentIds }));
    return;
  }
  dispatchAction(openSidebarPanel(SidebarPanelContentIds.DATASETS));
  registerNewDataset({
    uuid: uuidv4(),
    componentSelection: { startSet: componentIds },
  });
};

export const getTraversalMenuItems = (
  componentIds: ArdoqId[],
  trackingFn: TrackingFunction,
  i18nLocale: I18nLocale
): DropdownOption[] => {
  const multipleComponentTypesSelected =
    areMultipleComponentTypesSelected(componentIds);

  return [
    {
      name: 'start-traversal',
      testId: 'start-traversal-item',
      label: (
        <LabelWithMultipleComponentTypesSelectedForTraversalPopover
          shouldDisplayPopover={multipleComponentTypesSelected}
          i18nLocale={i18nLocale}
        >
          {t(
            i18nLocale,
            'contextMenus:openNewDataset',
            'Open new dataset from selection'
          )}
        </LabelWithMultipleComponentTypesSelectedForTraversalPopover>
      ),
      iconName: IconName.ACCOUNT_TREE,
      type: DropdownOptionType.OPTION,
      isDisabled: multipleComponentTypesSelected,
      onClick: () => {
        trackingFn(
          TrackedComponentContextMenuLabels.OPEN_NEW_DATASET_FROM_SELECTION
        );
        openNewDatasetFromSelection(componentIds);
      },
    },
    {
      name: 'apply-saved-traversal',
      testId: 'apply-saved-traversal-item',
      label: (
        <LabelWithMultipleComponentTypesSelectedForTraversalPopover
          shouldDisplayPopover={multipleComponentTypesSelected}
          i18nLocale={i18nLocale}
        >
          {t(
            i18nLocale,
            'contextMenus:applySavedViewpoint',
            'Apply a saved viewpoint'
          )}
        </LabelWithMultipleComponentTypesSelectedForTraversalPopover>
      ),
      iconName: IconName.VIEWPOINT,
      type: DropdownOptionType.OPTION,
      isDisabled: multipleComponentTypesSelected,
      onClick: () => {
        trackingFn(TrackedComponentContextMenuLabels.APPLY_A_SAVED_VIEWPOINT);
        dispatchAction(
          openApplySavedViewpointModalForComponentIds({ componentIds })
        );
      },
    },
  ].filter(isTruthy);
};

export const seeDetailsDrawer = (
  components: APIComponentAttributes[],
  isCanvasPrototype: boolean,
  isViewpointMode: boolean,
  i18nLocale: I18nLocale
) => {
  const componentIds = toIds(components);
  const multipleComponentTypesSelected =
    areMultipleComponentTypesSelected(componentIds);
  return {
    name: 'see-details-drawer',
    testId: 'see-details-drawer',
    label:
      isCanvasPrototype && isViewpointMode
        ? t(
            i18nLocale,
            'contextMenus:seeComponentOverview',
            'See component overview'
          )
        : t(
            i18nLocale,
            'contextMenus:seeComponentDetails',
            'See component details'
          ),
    iconName: IconName.FIELD,
    type: DropdownOptionType.OPTION,
    isDisabled: multipleComponentTypesSelected,
    onClick: () => {
      if (isCanvasPrototype && isViewpointMode) {
        openComponentOverviewDrawer({
          componentId: componentIds[0],
          trackingLocation: 'contextMenu',
          startTab: 'overview',
        });
      } else {
        dispatchAction(openDetailsDrawer(componentIds));
      }
    },
  };
};

export const OPEN_TRAVERSAL_VIEW_INFERENCE_NAME =
  'open-traversal-view-inference';

export const getTraversalInferenceMenuItems = (
  componentIds: ArdoqId[],
  i18nLocale: I18nLocale
): DropdownItem[] => {
  if (!hasFeature(Features.AZURE_OPENAI_VIEW_INFERENCE)) return [];
  const cannotInferFromSelection =
    mapToComponentTypeNames(componentIds).length !== 1;

  return [
    {
      name: OPEN_TRAVERSAL_VIEW_INFERENCE_NAME,
      label: (
        <LabelWithMultipleComponentTypesSelectedForTraversalPopover
          shouldDisplayPopover={cannotInferFromSelection}
          i18nLocale={i18nLocale}
        >
          {t(
            i18nLocale,
            'contextMenus:addNewDatasetWithAi',
            'Add new dataset with AI'
          )}
        </LabelWithMultipleComponentTypesSelectedForTraversalPopover>
      ),
      iconName: IconName.VIEW_INFERENCE,
      type: DropdownOptionType.OPTION,
      isDisabled: cannotInferFromSelection,
      onClick: event => {
        dispatchAction(
          openViewInferenceWindow({
            componentIds,
            initialPosition: { x: event.clientX, y: event.clientY },
          })
        );
      },
    },
    {
      name: 'divider',
      type: DropdownOptionType.DIVIDER,
    },
  ];
};

export const getLinkToLucidChartMenuItem = (
  componentId: ArdoqId,
  i18nLocale: I18nLocale
): DropdownItem[] => {
  if (!hasFeature(Features.LUCID_CHART_ENABLE)) return [];

  return [
    {
      name: 'link-lucidchart-document',
      label: t(
        i18nLocale,
        'contextMenus:linkLucidchartDocument',
        'Link Lucidchart document'
      ),
      iconName: IconName.LUCIDCHART,
      type: DropdownOptionType.OPTION,
      onClick: () => {
        dispatchAction(openDocumentPicker({ componentId }));
      },
    },
    {
      name: 'divider',
      type: DropdownOptionType.DIVIDER,
    },
  ];
};

function mapToComponentTypeNames(componentIds: string[]) {
  return uniq(componentIds.map(componentInterface.getTypeName)).filter(
    isTruthy
  );
}

function areMultipleComponentTypesSelected(componentIds: string[]) {
  const componentTypeNames = mapToComponentTypeNames(componentIds);
  return componentTypeNames.length > 1;
}

type LabelWithMultipleComponentTypesSelectedForTraversalPopoverProps = {
  shouldDisplayPopover: boolean;
  children: ReactNode;
  i18nLocale: I18nLocale;
};

const LabelWithMultipleComponentTypesSelectedForTraversalPopover = ({
  shouldDisplayPopover,
  children,
  i18nLocale,
}: LabelWithMultipleComponentTypesSelectedForTraversalPopoverProps) => {
  const popoverContent = () => {
    if (!shouldDisplayPopover) return null;

    return (
      <Stack>
        <Paragraph variant="text2Bold">
          {t(
            i18nLocale,
            'contextMenus:ai.popover.title',
            'Unable to add new dataset with AI'
          )}
        </Paragraph>
        <Paragraph variant="text2">
          {t(
            i18nLocale,
            'contextMenus:ai.popover.line1',
            "The selected component isn't connected to other components."
          )}
        </Paragraph>
        <Paragraph variant="text2">
          {t(
            i18nLocale,
            'contextMenus:ai.popover.line2',
            'Please select a different component to add a new dataset.'
          )}
        </Paragraph>
      </Stack>
    );
  };

  return (
    <WithPopover content={popoverContent}>
      <EnablePointerEventsWrapper>{children}</EnablePointerEventsWrapper>
    </WithPopover>
  );
};

const EnablePointerEventsWrapper = styled.div`
  pointer-events: all;
`;

export const getReferenceNavigateToMenuItem = (
  reference: APIReferenceAttributes,
  fullPathNamesById: Record<ArdoqId, string>,
  trackingFn: TrackingFunction,
  i18nLocale: I18nLocale
): DropdownSubmenu => {
  const referenceId = reference._id;
  const sourceComponentId = reference.source;
  const targetComponentId = reference.target;

  const referenceSourceFullPathName = fullPathNamesById[sourceComponentId];
  const referenceTargetFullPathName = fullPathNamesById[targetComponentId];
  const referenceName = `${referenceSourceFullPathName || 'Unknown'} -> ${referenceTargetFullPathName || 'Unknown'}`;

  return {
    name: 'navigate-to',
    label: t(i18nLocale, 'contextMenus:navigateTo', 'Navigate to'),
    iconName: IconName.NAVIGATE,
    type: DropdownOptionType.SUBMENU,
    options: [
      {
        name: 'go-to-reference',
        testId: 'go-to-reference-item',
        label: t(
          i18nLocale,
          'contextMenus:goToReference',
          { name: referenceName },
          'Go to {{name}}'
        ),
        type: DropdownOptionType.OPTION,
        onClick: () => {
          trackingFn(TrackedReferenceContextMenuLabels.SELECT_REFERENCE);
          dispatchAction(selectReference({ cid: referenceId }));
        },
      },
      sourceComponentId && {
        name: 'go-to-source-component',
        testId: 'go-to-source-component-item',
        label: t(
          i18nLocale,
          'contextMenus:goToSource',
          { name: referenceSourceFullPathName ?? '' },
          'Go to source {{name}}'
        ),
        type: DropdownOptionType.OPTION,
        onClick: () => {
          trackingFn(TrackedReferenceContextMenuLabels.SELECT_SOURCE_COMPONENT);
          dispatchAction(
            forceUpdateComponent({
              cid: sourceComponentId,
              trackingLocation: 'contextMenu',
            })
          );
        },
      },
      targetComponentId && {
        name: 'go-to-target-component',
        testId: 'go-to-target-component-item',
        label: t(
          i18nLocale,
          'contextMenus:goToTarget',
          { name: referenceTargetFullPathName ?? '' },
          'Go to target {{name}}'
        ),
        type: DropdownOptionType.OPTION,
        onClick: () => {
          trackingFn(TrackedReferenceContextMenuLabels.SELECT_TARGET_COMPONENT);
          dispatchAction(
            forceUpdateComponent({
              cid: targetComponentId,
              trackingLocation: 'contextMenu',
            })
          );
        },
      },
    ]
      .filter(isTruthy)
      .map(option => ({ ...option, truncateTitle: true })),
  };
};

export const getDropdownDivider = (): DropdownOption => ({
  label: '',
  type: DropdownOptionType.DIVIDER,
});

export const getDeleteComponentMenuItems = (
  components: APIComponentAttributes[],
  trackingFn: TrackingFunction,
  getNextTreeSelectionCandidate: () => ArdoqId | null,
  isCanvasPrototype: boolean,
  i18nLocale: I18nLocale,
  provenanceSource?: ProvenanceSource
): DropdownOption[] => {
  if (
    lockOperations.isAnyLocked(
      components.map(c => c.lock),
      getFeatureContext()
    )
  ) {
    return [];
  }
  const componentIds = toIds(components);

  const scenarioId = getActiveScenarioId();
  const isMultiselect = componentIds.length > 1;
  const label =
    scenarioId && !isMultiselect
      ? t(
          i18nLocale,
          'contextMenus:deleteComponentInScenario',
          'Delete component in scenario'
        )
      : isMultiselect
        ? t(i18nLocale, 'contextMenus:deleteComponents', 'Delete components')
        : t(i18nLocale, 'contextMenus:deleteComponent', 'Delete component');

  const trackingTitle = scenarioId
    ? TrackedComponentContextMenuLabels.DELETE_COMPONENT_IN_SCENARIO
    : TrackedComponentContextMenuLabels.DELETE_COMPONENT;

  const onRemoveFromScenarioClick = (componentIds: ArdoqId[]) => {
    if (allComponentsInScopeAreSelected(componentIds)) {
      return alert({
        title: t(
          i18nLocale,
          'contextMenus:excludeAllNotAllowedTitle',
          'Excluding all components not allowed'
        ),
        text: t(
          i18nLocale,
          'contextMenus:excludeAllNotAllowedText',
          'Excluding all the components in a scenario will break the scenario and is not allowed. Start a new scenario if you want to have a new scenario without any components.'
        ),
      });
    }

    trackingFn(
      TrackedComponentContextMenuLabels.EXCLUDE_COMPONENT_FROM_SCENARIO
    );
    removeComponentsFromScenario(componentIds);
  };

  const shouldUseErrorStyling = isCanvasPrototype;
  return [
    scenarioId && {
      name: 'exclude-item-from-scenario',
      testId: 'exclude-component-from-scenario-item',
      label: isMultiselect
        ? t(
            i18nLocale,
            'contextMenus:excludeComponentsFromScenario',
            'Exclude components from scenario'
          )
        : t(
            i18nLocale,
            'contextMenus:excludeComponentFromScenario',
            'Exclude component from scenario'
          ),
      iconName: IconName.REMOVE,
      type: DropdownOptionType.OPTION,
      onClick: () => onRemoveFromScenarioClick(componentIds),
    },

    {
      name: 'delete-item',
      testId: 'delete-component-item',
      label: shouldUseErrorStyling ? (
        <InlineText color="textError">{label}</InlineText>
      ) : (
        label
      ),
      leftIcon: {
        name: IconName.DELETE,
        ...(shouldUseErrorStyling && { color: colors.iconError }),
      },
      type: DropdownOptionType.OPTION,
      onClick: () => {
        trackingFn(trackingTitle);
        deleteModels({
          componentIds,
          treeSelectionCandidateId: getNextTreeSelectionCandidate(),
          provenanceSource,
        });
      },
    },
  ].filter(isTruthy);
};

export const getDeleteReferenceMenuItem = (
  referenceIds: ArdoqId[],
  trackingFn: TrackingFunction,
  i18nLocale: I18nLocale,
  shouldHaveRedIconAndText?: boolean
): DropdownOption => {
  const label =
    referenceIds.length === 1
      ? t(i18nLocale, 'contextMenus:deleteReference', 'Delete reference')
      : t(i18nLocale, 'contextMenus:deleteReferences', 'Delete references');

  return {
    name: 'delete-references',
    testId: 'delete-reference-item',
    label: shouldHaveRedIconAndText ? (
      <InlineText color="textError">{label}</InlineText>
    ) : (
      label
    ),
    leftIcon: {
      name: IconName.DELETE,
      ...(shouldHaveRedIconAndText && { color: colors.iconError }),
    },
    type: DropdownOptionType.OPTION,
    onClick: () => {
      trackingFn(TrackedReferenceContextMenuLabels.DELETE_REFERENCE);
      deleteModels({ referenceIds });
    },
  };
};

export const getDeleteMixedEntitiesItem = (
  componentIds: ArdoqId[],
  referenceIds: ArdoqId[],
  trackingFn: TrackingFunction,
  i18nLocale: I18nLocale,
  isViewpointMode: boolean
): DropdownOption => {
  // KFQ12026
  const shouldUseErrorStyling =
    isViewpointMode && hasFeature(Features.CANVAS_PROTOTYPE);
  const label = shouldUseErrorStyling ? (
    <InlineText color="textError">
      {t(i18nLocale, 'contextMenus:deleteAll', 'Delete all')}
    </InlineText>
  ) : (
    t(i18nLocale, 'contextMenus:delete', 'Delete')
  );

  return {
    name: 'delete-mixed-entities',
    testId: 'delete-mixed-entities-item',
    label,
    leftIcon: {
      name: IconName.DELETE,
      ...(shouldUseErrorStyling && { color: colors.iconError }),
    },
    type: DropdownOptionType.OPTION,
    onClick: () => {
      trackingFn(TrackedCompAndRefContextMenuLabels.DELETE);
      deleteModels({ referenceIds, componentIds });
    },
  };
};

export const getNewDatasetMenuItem = (
  componentIds: string[],
  i18nLocale: I18nLocale
): DropdownOption => ({
  name: 'new-dataset',
  testId: 'new-dataset-item',
  label: t(i18nLocale, 'contextMenus:newDataset', 'New dataset'),
  iconName: IconName.DATASET,
  type: DropdownOptionType.OPTION,
  onClick: () => {
    dispatchAction(
      openViewpointBuilderWithComponents({
        componentIds,
      })
    );
  },
});

export const getNewDatasetSubmenu = (
  componentIds: string[],
  trackingFn: TrackingFunction,
  i18nLocale: I18nLocale
): DropdownSubmenu | null => {
  const options = getTraversalMenuItems(componentIds, trackingFn, i18nLocale);
  if (!options.length) return null;

  return {
    name: 'add-dataset-to-view',
    label: t(
      i18nLocale,
      'contextMenus:addDatasetToView',
      'Add dataset to view'
    ),
    type: DropdownOptionType.SUBMENU,
    leftIcon: { name: IconName.DATASET },
    options,
  };
};

/**
 * "Exclude component".
 *
 * Viewpoint mode only, behind a flag. Workspace mode has its own exclude under the Filter submenu,
 * which writes a client-side perspective filter; this writes a traversal filter and re-runs the
 * traversal, so unlike that one it also drops the references and the components left disconnected.
 *
 * The way back is not here. It lives in the viewpoint toolbar, which shows a standing count of
 * what is hidden and is not gated by the flag — an exclusion is enforced server-side for whoever
 * opens the viewpoint, so the control that lifts it has to be reachable either way.
 */
export const getExcludeComponentMenuItem = (
  components: APIComponentAttributes[],
  i18nLocale: I18nLocale
): DropdownOption | null => {
  if (!hasFeature(Features.EXCLUDE_COMPONENT_FROM_VIEWPOINT)) return null;
  if (components.length !== 1) return null;

  const [component] = components;
  return {
    name: 'exclude-component',
    testId: 'exclude-component-item',
    label: t(
      i18nLocale,
      'contextMenus:excludeComponent.label',
      'Exclude component'
    ),
    iconName: IconName.VISIBILITY_OFF,
    type: DropdownOptionType.OPTION,
    onClick: () =>
      dispatchAction(
        excludeComponentFromViewpoint({
          componentId: component._id,
          componentName: component.name,
        })
      ),
  };
};

type DragToCreateReferenceMenuItemArgs = {
  componentIds: ArdoqId[];
  /** Where the menu was opened, and so where the tether starts. */
  x: number;
  y: number;
  /** What the gesture follows: an element, or the node a canvas view names. */
  target: Element | null;
  sourceNode: ContextMenuSourceNode | undefined;
  isCreatingReference: boolean;
  trackingFn: TrackingFunction;
  i18nLocale: I18nLocale;
};

export const getDragToCreateReferenceMenuItem = ({
  componentIds,
  x,
  y,
  target,
  sourceNode,
  isCreatingReference,
  trackingFn,
  i18nLocale,
}: DragToCreateReferenceMenuItemArgs): DropdownOption | null => {
  if (!hasFeature(Features.REFERENCE_CREATION_NOODLE)) return null;
  if (componentIds.length !== 1) return null;

  const sourceComponentId = componentIds[0];
  // Refused while the last reference is still on its way into the view: until
  // it is there, the next picker cannot tell it already exists and would offer
  // to create it again.
  return {
    name: 'drag-to-create-reference',
    testId: 'drag-to-create-reference-item',
    label: t(i18nLocale, 'contextMenus:drawReference', 'Draw reference'),
    iconName: IconName.REFERENCE,
    type: DropdownOptionType.OPTION,
    isDisabled: isCreatingReference,
    tooltipText: isCreatingReference
      ? t(
          i18nLocale,
          'contextMenus:dragToCreateReferenceWhileCreating',
          'Wait for the reference you just created to appear.'
        )
      : undefined,
    onClick: () => {
      trackingFn(TrackedComponentContextMenuLabels.DRAG_TO_CREATE_REFERENCE);
      dispatchAction(
        dynamicReferenceCreationArmed({
          sourceComponentId,
          startPosition: { x, y },
          sourceHandle: resolveSourceHandle(target, sourceNode),
        })
      );
    },
  };
};

export const getHistoryMenuItem = (
  entityId: ArdoqId,
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  trackingFn: TrackingFunction,
  i18nLocale: I18nLocale
): DropdownOption => ({
  name: 'view-history',
  testId: 'view-history-item',
  label: t(i18nLocale, 'contextMenus:viewHistory', 'View history'),
  iconName: IconName.HISTORY,
  type: DropdownOptionType.OPTION,
  isDisabled: isScenarioMode(),
  onClick: () => {
    trackingFn(TrackedComponentContextMenuLabels.VIEW_DIFF);
    trackAuditLogEntryPoint(`${entityType} history`);
    dispatchAction(
      navigateToAuditLog({
        entities: [{ id: entityId }],
        entityType,
      })
    );
  },
});

/**
 * If components are grouped by component type, the group node is not a component,
 * so only filter options for the component type are offered.
 */
type FilterMenuTarget =
  | {
      component: APIComponentAttributes;
      workspaceAndModel: WorkspaceAndModelLookup;
    }
  | { componentTypeGroupName: string };

type FilterMenuTargetNames = {
  componentId: ArdoqId;
  componentName: string | null;
  componentTypeName: string | null;
  workspaceId: ArdoqId | null;
  workspaceName: string | null;
};

const getFilterMenuTargetNames = (
  target: FilterMenuTarget
): FilterMenuTargetNames => {
  if ('componentTypeGroupName' in target) {
    return {
      componentId: '',
      componentName: null,
      componentTypeName: target.componentTypeGroupName,
      workspaceId: null,
      workspaceName: null,
    };
  }
  const { component, workspaceAndModel } = target;
  return {
    componentId: component._id,
    componentName: component.name,
    componentTypeName:
      scopeDataOperations.getComponentTypeFromWorkspaceLookup(
        workspaceAndModel,
        component
      )?.name ?? null,
    workspaceId: component.rootWorkspace,
    workspaceName:
      scopeDataOperations.getWorkspace(
        workspaceAndModel,
        component.rootWorkspace
      )?.name ?? null,
  };
};

type GetFilterMenuItemArgs = {
  target: FilterMenuTarget;
  trackingFunction: TrackingFunction;
  isScenarioContextComponent?: boolean;
  i18nLocale: I18nLocale;
  isViewpointMode: boolean;
};

export const getFilterMenuItem = ({
  target,
  trackingFunction,
  isScenarioContextComponent,
  i18nLocale,
  isViewpointMode,
}: GetFilterMenuItemArgs) => {
  const {
    componentId,
    componentName,
    componentTypeName,
    workspaceId,
    workspaceName,
  } = getFilterMenuTargetNames(target);

  return {
    name: 'filter-list',
    label: (
      <div
        style={{
          wordBreak: 'keep-all',
        }}
      >
        {t(i18nLocale, 'contextMenus:filter', 'Filter')}
      </div>
    ),
    intercomTarget: 'Filter',
    iconName: IconName.FILTER_LIST,
    type: DropdownOptionType.SUBMENU,
    options: getFilterMenuItemOptions({
      componentId,
      componentName,
      componentTypeName,
      workspaceId,
      workspaceName,
      trackingFunction,
      isScenarioContextComponent,
      i18nLocale,
      isViewpointMode,
    }),
  };
};

type GetFilterMenuItemOptionsArgs = {
  componentId?: ArdoqId;
  componentName?: string | null;
  componentTypeName?: string | null;
  workspaceId?: string | null;
  workspaceName?: string | null;
  trackingFunction?: TrackingFunction;
  isScenarioContextComponent?: boolean;
  i18nLocale: I18nLocale;
  isViewpointMode: boolean;
};

const getFilterMenuItemOptions = ({
  componentId,
  componentName,
  componentTypeName,
  workspaceId,
  workspaceName,
  trackingFunction,
  isScenarioContextComponent,
  i18nLocale,
  isViewpointMode,
}: GetFilterMenuItemOptionsArgs) => {
  const TrackedMenuLabels = isScenarioContextComponent
    ? TrackedScenarioContextComponentContextMenuLabels
    : TrackedComponentContextMenuLabels;

  return [
    componentId && {
      name: 'exclude-selected',
      testId: 'exclude-from-context-item',
      label: t(
        i18nLocale,
        'contextMenus:excludeName',
        { name: componentName ?? '' },
        'Exclude "{{name}}"'
      ),
      type: DropdownOptionType.OPTION,
      onClick: () => {
        trackingFunction?.(TrackedMenuLabels.EXCLUDE_FROM_CONTEXT);
        excludeSelected(componentId, false, componentName ?? undefined);
      },
    },
    componentTypeName && {
      name: 'include-component-type',
      testId: 'show-components-of-type-item',
      label: t(
        i18nLocale,
        'contextMenus:showComponentType',
        { type: componentTypeName },
        'Show component type "{{type}}"'
      ),
      type: DropdownOptionType.OPTION,
      onClick: () => {
        trackingFunction?.(TrackedMenuLabels.SHOW_ONLY_COMPONENTS_OF_THIS_TYPE);
        includeComponentsOfType(componentTypeName);
      },
    },
    componentTypeName && {
      name: 'exclude-component-type',
      testId: 'exclude-components-of-type-item',
      label: t(
        i18nLocale,
        'contextMenus:excludeComponentType',
        { type: componentTypeName },
        'Exclude component type "{{type}}"'
      ),
      type: DropdownOptionType.OPTION,
      onClick: () => {
        trackingFunction?.(TrackedMenuLabels.EXCLUDE_COMPONENTS_OF_THIS_TYPE);
        excludeComponentsOfType(componentTypeName);
      },
    },
    componentId &&
      componentName && {
        name: 'exclude-child-components',
        testId: 'exclude-child-components-item',
        label: t(
          i18nLocale,
          'contextMenus:excludeChildComponents',
          { name: componentName },
          'Exclude child components "{{name}}"'
        ),
        type: DropdownOptionType.OPTION,
        onClick: () => {
          trackingFunction?.(TrackedMenuLabels.EXCLUDE_CHILD_COMPONENTS);
          excludeChildComponents(componentId, componentName ?? undefined);
        },
      },
    workspaceId &&
      !isViewpointMode && {
        name: 'exclude-content-from-workspace',
        testId: 'exclude-content-from-workspace-item',
        label: t(
          i18nLocale,
          'contextMenus:excludeContentFromWorkspace',
          { name: workspaceName ?? '' },
          'Exclude content from workspace "{{name}}"'
        ),
        type: DropdownOptionType.OPTION,
        onClick: () => {
          trackingFunction?.(TrackedMenuLabels.EXCLUDE_CONTENT_FROM_WORKSPACE);
          excludeContentFromWorkspace(workspaceId, workspaceName ?? undefined);
        },
      },
  ]
    .filter(isTruthy)
    .map(option => ({ ...option, truncateTitle: true }));
};

type GetFilterReferenceMenuItemArgs = {
  reference: APIReferenceAttributes;
  endpoints: ReferenceEndpoints;
  fullPathNamesById: Record<ArdoqId, string>;
  workspaceAndModel: WorkspaceAndModelLookup;
  trackingFn: TrackingFunction;
  i18nLocale: I18nLocale;
};

export const getFilterReferenceMenuItem = ({
  reference,
  endpoints: { source, target },
  fullPathNamesById,
  workspaceAndModel,
  trackingFn,
  i18nLocale,
}: GetFilterReferenceMenuItemArgs) => {
  const referenceId = reference._id;
  const referenceTypeName = getReferenceTypeNameFromModels(
    reference,
    workspaceAndModel
  );
  const sourceName = source?.name;
  const targetName = target?.name;
  const referenceTargetWorkspaceId = target?.rootWorkspace ?? null;
  const referenceTargetWorkspaceName = referenceTargetWorkspaceId
    ? (scopeDataOperations.getWorkspace(
        workspaceAndModel,
        referenceTargetWorkspaceId
      )?.name ?? null)
    : null;

  return {
    name: 'filter-references',
    label: t(i18nLocale, 'contextMenus:filter', 'Filter'),
    iconName: IconName.FILTER_LIST,
    type: DropdownOptionType.SUBMENU,
    options: [
      {
        name: 'exclude-reference',
        testId: 'exclude-reference-item',
        label: t(
          i18nLocale,
          'contextMenus:excludeThisReference',
          'Exclude this reference'
        ),
        type: DropdownOptionType.OPTION,
        onClick: () => {
          trackingFn(TrackedReferenceContextMenuLabels.EXCLUDE_FROM_CONTEXT);
          excludeSelected(
            referenceId,
            true,
            getReferenceDisplayName(reference, fullPathNamesById)
          );
        },
      },
      referenceTypeName && {
        name: 'exclude-references-of-type',
        testId: 'exclude-references-of-type-item',
        label: t(
          i18nLocale,
          'contextMenus:excludeReferencesOfType',
          { type: referenceTypeName },
          'Exclude references of type "{{type}}"'
        ),
        type: DropdownOptionType.OPTION,
        onClick: () => {
          trackingFn(
            TrackedReferenceContextMenuLabels.EXCLUDE_REFERENCES_OF_TYPE
          );
          excludeReferencesOfType(referenceTypeName);
        },
      },
      referenceTypeName && {
        name: 'show-references-of-type',
        testId: 'show-references-of-type-item',
        label: t(
          i18nLocale,
          'contextMenus:showReferencesOfType',
          { type: referenceTypeName },
          'Show references of type "{{type}}"'
        ),
        type: DropdownOptionType.OPTION,
        onClick: () => {
          trackingFn(
            TrackedReferenceContextMenuLabels.SHOW_ONLY_REFERNCES_OF_TYPE
          );
          includeReferencesOfType(referenceTypeName);
        },
      },
      target && {
        name: 'show-references-to-target',
        testId: 'show-references-to-target-item',
        label: t(
          i18nLocale,
          'contextMenus:showReferencesToTarget',
          { name: targetName ?? '' },
          'Show references to target "{{name}}"'
        ),
        type: DropdownOptionType.OPTION,
        onClick: () => {
          trackingFn(
            TrackedReferenceContextMenuLabels.SHOW_ONLY_REFERNCES_TO_TARGET
          );
          createReferencesToTargetFilter(target._id, targetName ?? undefined);
        },
      },
      source && {
        name: 'show-references-from-source',
        testId: 'show-references-from-source-item',
        label: t(
          i18nLocale,
          'contextMenus:showReferencesFromSource',
          { name: sourceName ?? '' },
          'Show references from source "{{name}}"'
        ),
        type: DropdownOptionType.OPTION,
        onClick: () => {
          trackingFn(
            TrackedReferenceContextMenuLabels.SHOW_ONLY_REFERENCES_FROM_SOURCE
          );
          createReferencesFromSourceFilter(source._id, sourceName ?? undefined);
        },
      },
      referenceTargetWorkspaceId && {
        name: 'exclude-content-from-reference-target-workspace',
        testId: 'exclude-content-from-reference-target-workspace-item',
        label: t(
          i18nLocale,
          'contextMenus:excludeContentFrom',
          { name: referenceTargetWorkspaceName ?? '' },
          'Exclude content from "{{name}}"'
        ),
        type: DropdownOptionType.OPTION,
        onClick: () => {
          trackingFn(
            TrackedReferenceContextMenuLabels.EXCLUDE_CONTENT_FROM_WORKSPACE
          );
          excludeContentFromWorkspace(
            referenceTargetWorkspaceId,
            referenceTargetWorkspaceName || undefined
          );
        },
      },
    ]
      .filter(isTruthy)
      .map(option => ({ ...option, truncateTitle: true })),
  };
};

export const getMergeEntitiesMenuItem = (
  entityIds: ArdoqId[],
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  trackingFn: TrackingFunction,
  i18nLocale: I18nLocale
): DropdownOption => {
  const isComponent = entityType === APIEntityType.COMPONENT;
  const trackingOptionTitle = isComponent
    ? TrackedComponentContextMenuLabels.MERGE_COMPONENTS
    : TrackedReferenceContextMenuLabels.MERGE_REFERENCES;

  return {
    name: 'merge-entities',
    testId: 'merge-entities-item',
    label: isComponent
      ? t(i18nLocale, 'contextMenus:mergeComponents', 'Merge components')
      : t(i18nLocale, 'contextMenus:mergeReferences', 'Merge references'),
    iconName: IconName.SCENARIO_MERGE,
    type: DropdownOptionType.OPTION,
    onClick: () => {
      trackingFn(trackingOptionTitle);
      dispatchAction(
        initEntityMerge({
          entityIds,
          entityType,
        })
      );
    },
  };
};

type ReverseReferencesPopoverContentProps = {
  hasBlockedViolations: boolean;
  hasWarningViolations: boolean;
  isSingleViolation: boolean;
  displayedViolations: ReverseConstraintViolation[];
  totalViolationCount: number;
  i18nLocale: I18nLocale;
};

const toReverseReferencesPopoverContentProps = (
  violations: ReverseConstraintViolation[],
  i18nLocale: I18nLocale
): ReverseReferencesPopoverContentProps => ({
  hasBlockedViolations: violations.some(v => v.isBlocked),
  hasWarningViolations: violations.some(v => v.isWarning),
  isSingleViolation: violations.length === 1,
  displayedViolations: violations.slice(0, 3),
  totalViolationCount: violations.length,
  i18nLocale,
});

const ReverseReferencesPopoverContent = ({
  hasBlockedViolations,
  hasWarningViolations,
  isSingleViolation,
  displayedViolations,
  totalViolationCount,
  i18nLocale,
}: ReverseReferencesPopoverContentProps): React.ReactNode => {
  if (totalViolationCount === 0) {
    return null;
  }
  return (
    <Stack>
      {hasBlockedViolations && (
        <Paragraph variant="text2Bold">
          {t(
            i18nLocale,
            'contextMenus:reverse.blockedByConstraints',
            'Reverse is blocked by metamodel constraints'
          )}
        </Paragraph>
      )}
      {hasWarningViolations && (
        <>
          <Paragraph variant="text2Bold">
            {t(
              i18nLocale,
              'contextMenus:reverse.violatesConstraints',
              'Violates metamodel constraints'
            )}
          </Paragraph>
          <Paragraph variant="caption">
            {isSingleViolation
              ? t(
                  i18nLocale,
                  'contextMenus:reverse.wouldCreateRelationships.one',
                  'Reversing the following reference would create a relationship that is not allowed by the metamodel.'
                )
              : t(
                  i18nLocale,
                  'contextMenus:reverse.wouldCreateRelationships.other',
                  'Reversing the following references would create relationships that are not allowed by the metamodel.'
                )}
          </Paragraph>
          <br />
        </>
      )}
      {displayedViolations.map((v, i) => (
        <Paragraph key={i} variant="caption">
          {v.sourceName} ({v.sourceTypeName}) → {v.referenceTypeName} →{' '}
          {v.targetName} ({v.targetTypeName})
        </Paragraph>
      ))}
      {totalViolationCount > 3 && (
        <Paragraph variant="caption">
          {t(
            i18nLocale,
            'contextMenus:reverse.andMore',
            { count: totalViolationCount - 3 },
            'And {{count}} more...'
          )}
        </Paragraph>
      )}
    </Stack>
  );
};

type GetReverseReferenceMenuItemArgs = {
  referenceComponents: ReferenceComponents;
  workspaceAndModel: WorkspaceAndModelLookup;
  referenceCreation: ReferenceCreationMenuState;
  trackingFn: TrackingFunction;
  canEditAllReferencesTargets: boolean;
  i18nLocale: I18nLocale;
};

export const getReverseReferenceMenuItem = ({
  referenceComponents,
  workspaceAndModel,
  referenceCreation: { intendedRefTypes, constraintEnforcementMode },
  trackingFn,
  canEditAllReferencesTargets,
  i18nLocale,
}: GetReverseReferenceMenuItemArgs): DropdownOption | null => {
  if (!canEditAllReferencesTargets) {
    return null;
  }
  const referenceIds = toIds(referenceComponents.references);
  const constraintViolations = getReverseConstraintViolations({
    referenceComponents,
    workspaceAndModel,
    intendedRefTypes,
    constraintEnforcementMode,
  });

  const isStrictMode = constraintEnforcementMode === 'strict';

  const disableReverseDueToViolations =
    constraintViolations.some(violation => violation.isBlocked) && isStrictMode;

  const wouldViolateConstraints = constraintViolations.length > 0;

  return {
    name: 'reverse-references',
    testId: 'reverse-references-item',
    popoverProps: wouldViolateConstraints
      ? {
          content: () => (
            <ReverseReferencesPopoverContent
              {...toReverseReferencesPopoverContentProps(
                constraintViolations,
                i18nLocale
              )}
            />
          ),
        }
      : undefined,
    label:
      referenceIds.length === 1
        ? t(i18nLocale, 'contextMenus:reverseReference', 'Reverse reference')
        : t(i18nLocale, 'contextMenus:reverseReferences', 'Reverse references'),
    iconName: IconName.SWAP_HORIZONTAL,
    type: DropdownOptionType.OPTION,
    isDisabled: disableReverseDueToViolations,
    onClick: () => {
      trackingFn(TrackedReferenceContextMenuLabels.REVERSE);
      dispatchAction(reverseReferences(referenceIds));
    },
  };
};

export const getPermissionsMenuItem = (
  workspaceId: ArdoqId,
  workspaceName: string | undefined,
  trackingFn: TrackingFunction,
  i18nLocale: I18nLocale
): DropdownOption | null => {
  if (!workspaceName) {
    return null;
  }

  return {
    name: 'permissions',
    testId: 'permissions-item',
    label: t(i18nLocale, 'contextMenus:permissions', 'Permissions'),
    iconName: IconName.PERSON,
    isDisabled: isScenarioMode(),
    type: DropdownOptionType.OPTION,
    onClick: () => {
      trackingFn(TrackedWorkspaceContextMenuLabels.PERMISSIONS);
      dispatchAction(
        navigate({
          route: '/workspace-manager/:workspaceId/permissions',
          params: { workspaceId },
        })
      );
    },
  };
};

export const getEditWorkspaceMenuItem = (
  workspaceId: ArdoqId,
  trackingFn: TrackingFunction,
  isCanvasPrototype: boolean,
  i18nLocale: I18nLocale
): DropdownOption => ({
  name: 'edit-workspace',
  testId: 'edit-workspace-item',
  label: isCanvasPrototype
    ? t(i18nLocale, 'contextMenus:workspaceManager', 'Workspace manager')
    : t(i18nLocale, 'contextMenus:editWorkspace', 'Edit workspace'),
  leftIcon: {
    name: IconName.EDIT,
  },
  type: DropdownOptionType.OPTION,
  isDisabled: isScenarioMode(),
  onClick: () => {
    trackingFn(TrackedWorkspaceContextMenuLabels.EDIT_WORKSPACE);
    dispatchAction(
      navigate({
        route: '/workspace-manager/:workspaceId/settings',
        params: { workspaceId },
      })
    );
  },
});

export const getEditMetamodelMenuItem = (
  workspaceId: ArdoqId,
  trackingFn: TrackingFunction,
  i18nLocale: I18nLocale
): DropdownOption => ({
  name: 'edit-metamodel',
  testId: 'edit-metamodel-item',
  label: t(i18nLocale, 'contextMenus:editMetamodel', 'Edit metamodel'),
  iconName: IconName.EDIT,
  type: DropdownOptionType.OPTION,
  isDisabled: isScenarioMode(),
  onClick: () => {
    trackingFn(TrackedWorkspaceContextMenuLabels.EDIT_METAMODEL);
    dispatchAction(
      navigate({
        route: '/workspace-manager/:workspaceId/metamodel/component-types',
        params: { workspaceId },
      })
    );
  },
});

export const getExcludeWorkspaceMenuItem = (
  scenarioId: ArdoqId,
  workspaceId: ArdoqId,
  isDisabled: boolean,
  trackingFn: TrackingFunction,
  i18nLocale: I18nLocale
): DropdownOption => ({
  name: 'exclude-workspace-from-scenario',
  testId: 'exclude-workspace-from-scenario-item',
  label: t(
    i18nLocale,
    'contextMenus:excludeWorkspaceFromScenario',
    'Exclude workspace from scenario'
  ),
  iconName: IconName.REMOVE,
  type: DropdownOptionType.OPTION,
  isDisabled,
  onClick: () => {
    trackingFn(
      TrackedWorkspaceContextMenuLabels.EXCLUDE_WORKSPACE_FROM_SCENARIO
    );
    dispatchAction(excludeWorkspaceFromScenario({ scenarioId, workspaceId }));
  },
});

export const getFilterWorkspaceMenuItem = (
  workspaceId: ArdoqId,
  workspaceName: string,
  trackingFn: TrackingFunction,
  i18nLocale: I18nLocale
): DropdownSubmenu => {
  return {
    name: 'workspace-filter',
    label: t(i18nLocale, 'contextMenus:filter', 'Filter'),
    iconName: IconName.FILTER_LIST,
    type: DropdownOptionType.SUBMENU,
    options: [
      {
        name: 'exclude-content-from-workspace-filter',
        testId: 'exclude-workspace-content-item',
        label: t(
          i18nLocale,
          'contextMenus:filterOutContent',
          { name: workspaceName },
          'Filter out content from "{{name}}"'
        ),
        type: DropdownOptionType.OPTION,
        onClick: () => {
          trackingFn(
            TrackedWorkspaceContextMenuLabels.EXCLUDE_CONTENT_FROM_WORKSPACE
          );
          excludeContentFromWorkspace(workspaceId, workspaceName);
        },
      },
      {
        name: 'exclude-references-to-workspace-filter',
        testId: 'exclude-references-to-item',
        label: t(
          i18nLocale,
          'contextMenus:filterOutReferencesTo',
          { name: workspaceName },
          'Filter out references to workspace "{{name}}"'
        ),
        type: DropdownOptionType.OPTION,
        onClick: () => {
          trackingFn(
            TrackedWorkspaceContextMenuLabels.EXCLUDE_REFERENCES_TO_WORKSPACE
          );
          excludeReferencesToWorkspace(workspaceId, workspaceName);
        },
      },
      {
        name: 'exclude-references-from-workspace-filter',
        testId: 'exclude-references-from-item',
        label: t(
          i18nLocale,
          'contextMenus:filterOutReferencesFrom',
          { name: workspaceName },
          'Filter out references from workspace "{{name}}"'
        ),
        type: DropdownOptionType.OPTION,
        onClick: () => {
          trackingFn(
            TrackedWorkspaceContextMenuLabels.EXCLUDE_REFERENCES_FROM_WORKSPACE
          );
          excludeReferencesFromWorkspace(workspaceId, workspaceName);
        },
      },
    ],
  };
};

export const getEditTagMenuItem = (
  tagId: ArdoqId,
  trackingFn: TrackingFunction,
  i18nLocale: I18nLocale
): DropdownOption => ({
  name: 'edit-tag',
  testId: 'edit-tag-item',
  label: t(i18nLocale, 'contextMenus:editTag', 'Edit tag'),
  iconName: IconName.EDIT,
  type: DropdownOptionType.OPTION,
  onClick: () => {
    dispatchAction(editTag(tagId));
    trackingFn(TrackedTagContextMenuLabels.EDIT_TAG);
  },
});

export const getDeleteTagMenuItem = (
  tagId: ArdoqId,
  trackingFn: TrackingFunction,
  i18nLocale: I18nLocale
): DropdownOption => ({
  name: 'delete-tag',
  testId: 'delete-tag-item',
  label: t(i18nLocale, 'contextMenus:deleteTag', 'Delete tag'),
  iconName: IconName.EDIT,
  type: DropdownOptionType.OPTION,
  onClick: () => {
    dispatchAction(deleteTag(tagId));
    trackingFn(TrackedTagContextMenuLabels.DELETE_TAG);
  },
});

export const getFilterTagMenuItem = (
  tagId: ArdoqId,
  trackingFn: TrackingFunction,
  i18nLocale: I18nLocale
): DropdownSubmenu | null => {
  return {
    name: 'tag-filter',
    label: t(i18nLocale, 'contextMenus:filter', 'Filter'),
    iconName: IconName.FILTER_LIST,
    type: DropdownOptionType.SUBMENU,
    options: [
      {
        name: 'filter-show-content-with-tag',
        testId: 'show-content-with-tag-item',
        label: t(
          i18nLocale,
          'contextMenus:showContentWithTag',
          'Show content with tag'
        ),
        type: DropdownOptionType.OPTION,
        onClick: () => {
          trackingFn(TrackedTagContextMenuLabels.SHOW_CONTENT_WITH_TAG);
          dispatchAction(filterTag({ id: tagId, exclude: false }));
        },
      },
      {
        name: 'filter-exclude-content-with-tag',
        testId: 'exclude-content-with-tag-item',
        label: t(
          i18nLocale,
          'contextMenus:excludeContentWithTag',
          'Exclude content with tag'
        ),
        type: DropdownOptionType.OPTION,
        onClick: () => {
          trackingFn(TrackedTagContextMenuLabels.EXCLUDE_CONTENT_WITH_TAG);
          dispatchAction(filterTag({ id: tagId, exclude: true }));
        },
      },
    ],
  };
};

export const getRotateToolMenuItem = (
  controller: Controller,
  contextNode: BlocksViewNode,
  trackContextMenu: (openTitle: string) => void,
  i18nLocale: I18nLocale
) => ({
  type: DropdownOptionType.OPTION,
  label: t(i18nLocale, 'contextMenus:rotate', 'Rotate'),
  onClick: () => {
    trackContextMenu('rotate');
    controller.rotate([contextNode]);
  },
  iconName: IconName.ROTATE,
});

export const getFlipHorizontallyMenuItem = (
  controller: Controller,
  contextNode: BlocksViewNode,
  trackContextMenu: (openTitle: string) => void,
  i18nLocale: I18nLocale
) => ({
  type: DropdownOptionType.OPTION,
  label: t(i18nLocale, 'contextMenus:flipHorizontally', 'Flip Horizontally'),
  onClick: () => {
    trackContextMenu('flipHorizontally');
    controller.flipHorizontal([contextNode]);
  },
  iconName: IconName.FLIP_HORIZONTALLY,
});

export const getFlipVerticallyMenuItem = (
  controller: Controller,
  contextNode: BlocksViewNode,
  trackContextMenu: (openTitle: string) => void,
  i18nLocale: I18nLocale
) => ({
  type: DropdownOptionType.OPTION,
  label: t(i18nLocale, 'contextMenus:flipVertically', 'Flip Vertically'),
  onClick: () => {
    trackContextMenu('flipVertically');
    controller.flipVertical([contextNode]);
  },
  iconName: IconName.FLIP_VERTICALLY,
});

export const getBaseLayoutToolsMenuItems = (
  controller: Controller,
  contextNode: BlocksViewNode,
  trackContextMenu: (openTitle: string) => void,
  i18nLocale: I18nLocale
): DropdownItem[] => [
  getRotateToolMenuItem(controller, contextNode, trackContextMenu, i18nLocale),
  getFlipHorizontallyMenuItem(
    controller,
    contextNode,
    trackContextMenu,
    i18nLocale
  ),
  getFlipVerticallyMenuItem(
    controller,
    contextNode,
    trackContextMenu,
    i18nLocale
  ),
];
